import { useState } from 'react';
import { CheckCircle2, Globe, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '../components/sections/PageHeader';
import { CONTACT, ENGAGEMENT, INDUSTRIES, SERVICE_OPTIONS } from '../data/siteData';

const EMPTY = { name: '', email: '', company: '', sector: INDUSTRIES[2], message: '', services: [] };

export const ContactPage = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const toggleService = (service) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(service) ? f.services.filter((s) => s !== service) : [...f.services, service],
    }));

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = 'Enter your name.';
    if (!/^\S+@\S+\.\S+$/.test(form.email)) next.email = 'Enter a valid email address.';
    if (!form.message.trim()) next.message = 'Add a few details about the project.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    const subject = `BIM Services Inquiry - ${form.name} from ${form.company || 'Not specified'}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company || 'Not specified'}`,
      `Sector: ${form.sector}`,
      `Services: ${form.services.length ? form.services.join(', ') : 'Not specified'}`,
      '',
      'Project details:',
      form.message,
    ].join('\n');

    window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
    toast.success('Your email app has opened with the enquiry. Send it to complete your request.');
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Contact" title="Let's discuss your BIM requirements">
        Tell us about your project and we&apos;ll suggest a pilot package to start with.
      </PageHeader>

      <section className="sec white">
        <div className="wrap contact-grid">
          <div>
            <div className="hd">
              <div className="mono">Get in touch</div>
              <h2>BuildiTech Engineering Consultants</h2>
            </div>
            <div className="contact-info">
              <div className="row">
                <span className="ic"><Mail size={20} /></span>
                <div><small>Email</small><a href={`mailto:${CONTACT.email}`} data-testid="email-link">{CONTACT.email}</a></div>
              </div>
              <div className="row">
                <span className="ic"><MapPin size={20} /></span>
                <div><small>Office</small><span>{CONTACT.address}</span></div>
              </div>
              <div className="row">
                <span className="ic"><Globe size={20} /></span>
                <div><small>Website</small><span>{CONTACT.website}</span></div>
              </div>
            </div>
            <div className="steps-list" style={{ borderColor: 'var(--line)' }}>
              {ENGAGEMENT.map((e) => (
                <div key={e.title} style={{ color: 'var(--mut)' }}>
                  <b style={{ color: 'var(--navy)' }}>{e.title}</b> {e.text}
                </div>
              ))}
            </div>
          </div>

          <form className="enq" onSubmit={handleSubmit} noValidate data-testid="contact-form">
            <h3>Tell us about your project</h3>

            <div className="grp">
              <span className="lbl" id="svc-label">I need help with</span>
              <div className="opts" role="group" aria-labelledby="svc-label">
                {SERVICE_OPTIONS.map((s, i) => (
                  <span key={s}>
                    <input
                      type="checkbox"
                      id={`svc-${i}`}
                      checked={form.services.includes(s)}
                      onChange={() => toggleService(s)}
                    />
                    <label htmlFor={`svc-${i}`}>{s}</label>
                  </span>
                ))}
              </div>
            </div>

            <div className="grp row2">
              <div>
                <label htmlFor="f-name">Name</label>
                <input type="text" id="f-name" name="name" value={form.name} onChange={update} autoComplete="name" aria-invalid={!!errors.name} data-testid="contact-name-input" />
                {errors.name && <div className="err">{errors.name}</div>}
              </div>
              <div>
                <label htmlFor="f-email">Work email</label>
                <input type="email" id="f-email" name="email" value={form.email} onChange={update} autoComplete="email" aria-invalid={!!errors.email} data-testid="contact-email-input" />
                {errors.email && <div className="err">{errors.email}</div>}
              </div>
            </div>

            <div className="grp row2">
              <div>
                <label htmlFor="f-company">Company</label>
                <input type="text" id="f-company" name="company" value={form.company} onChange={update} autoComplete="organization" />
              </div>
              <div>
                <label htmlFor="f-sector">Sector</label>
                <select id="f-sector" name="sector" value={form.sector} onChange={update}>
                  {INDUSTRIES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grp">
              <label htmlFor="f-message">Project details</label>
              <textarea id="f-message" name="message" value={form.message} onChange={update} placeholder="Scope, disciplines, timeline…" aria-invalid={!!errors.message} data-testid="contact-message-input" />
              {errors.message && <div className="err">{errors.message}</div>}
            </div>

            <button type="submit" className="btn btn-p" data-testid="contact-submit-button">
              Send enquiry <span className="ar">→</span>
            </button>

            {submitted && (
              <div className="done" role="status">
                <CheckCircle2 size={18} style={{ flex: 'none', marginTop: 2 }} />
                <span>Your email app should now be open with the enquiry filled in. If it didn&apos;t open, email us at {CONTACT.email}.</span>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
